import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    Typography,
    Alert,
    Paper,
    Chip
} from '@mui/material';
import {
    Add as AddIcon,
    AccountBalance as BankIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'sonner';
import API from '../../../../../API';
import CompanySelector from './CompanySelector';
import BankAccountCard from './BankAccountCard';
import BankAccountLinkingDialog from './BankAccountLinkingDialog';

/**
 * Company Bank Accounts Management Section
 *
 * Admin-only interface for managing company internal bank accounts
 * for Stripe ACH reimbursement operations after creator payouts.
 */
function BankAccountsSection() {
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [linkingDialogOpen, setLinkingDialogOpen] = useState(false);
    const queryClient = useQueryClient();

    // Reset selected company on unmount
    useEffect(() => {
        return () => {
            setSelectedCompany(null);
        };
    }, []);

    // Fetch bank accounts for selected company
    const {
        data: bankAccounts,
        isLoading,
        error,
        refetch
    } = useQuery(
        ['companyBankAccounts', selectedCompany?.id],
        () => API.admin.getCompanyBankAccounts(selectedCompany.id),
        {
            enabled: !!selectedCompany?.id,
            onError: (error) => {
                toast.error(`Failed to load bank accounts: ${error.message}`);
            }
        }
    );

    // Handle account linking dialog
    const handleOpenLinkDialog = () => {
        if (!selectedCompany) {
            toast.error('Please select a company first');
            return;
        }
        setLinkingDialogOpen(true);
    };

    const handleCloseLinkDialog = () => {
        setLinkingDialogOpen(false);
    };

    const handleLinkSuccess = () => {
        setLinkingDialogOpen(false);
        refetch();
        toast.success('Bank account linked successfully');
    };

    // Handle refresh
    const handleRefresh = () => {
        refetch();
        toast.info('Refreshing bank accounts...');
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" gutterBottom>
                    <BankIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Company Bank Accounts
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Manage company internal bank accounts for automated ACH reimbursements.
                    These accounts are used by Blitz staff for internal financial operations.
                </Typography>
            </Box>

            {/* Company Selector */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <CompanySelector
                            key="company-selector"
                            value={selectedCompany}
                            onChange={setSelectedCompany}
                        />
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ textAlign: { md: 'right' } }}>
                        <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={handleOpenLinkDialog}
                                disabled={!selectedCompany}
                                sx={{ mr: 1 }}
                            >
                                Link Bank Account
                            </Button>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={handleRefresh}
                            disabled={!selectedCompany || isLoading}
                        >
                            Refresh
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Main Content */}
            {!selectedCompany ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                    Please select a company to view and manage bank accounts.
                </Alert>
            ) : isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Alert severity="error" sx={{ mt: 2 }}>
                    Failed to load bank accounts: {error.message}
                </Alert>
            ) : (
                <>
                    {/* Bank Accounts Grid */}
                    {bankAccounts?.accounts?.length > 0 ? (
                        <Grid container spacing={3}>
                            {bankAccounts.accounts.map((account) => (
                                <Grid item xs={12} md={6} lg={4} key={account.id}>
                                    <BankAccountCard
                                        account={account}
                                        companyId={selectedCompany?.id}
                                        onUpdate={() => refetch()}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Paper sx={{ p: 4, textAlign: 'center' }}>
                            <BankIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h6" gutterBottom>
                                No Bank Accounts Linked
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                This company doesn't have any bank accounts linked yet.
                                Link a bank account to get started.
                            </Typography>
                            <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={handleOpenLinkDialog}
                                >
                                    Link First Bank Account
                                </Button>
                        </Paper>
                    )}
                </>
            )}

            {/* Bank Account Linking Dialog */}
            <BankAccountLinkingDialog
                open={linkingDialogOpen}
                onClose={handleCloseLinkDialog}
                company={selectedCompany}
                onSuccess={handleLinkSuccess}
            />
        </Box>
    );
}

export default BankAccountsSection;
